import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';

import {Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsApplicationsDetailsUrl,
  SettingsAutolinkersUrl,
  SettingsOauthClientUrl,
  SettingsPreviewersUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
  SettingsServersUrl,
} from '../../../models/url/settingsUrlModel';
import {SettingsBreadcrumbs} from '../../common/settingsBreadcrumbs';
import {SettingsHeader} from '../../settingsHeader';
import {ApplicationsTab} from './applicationsTab';

interface ApplicationsHeaderProps {
  renderRight?: Renderer;
  installedApplication?: ApplicationModel;
  application?: LocalApplicationModel;
  url:
    | SettingsApplicationsDetailsUrl
    | SettingsAutolinkersUrl
    | SettingsPreviewersUrl
    | SettingsServersUrl
    | SettingsOauthClientUrl;
}

export const ApplicationsHeader: FC<ApplicationsHeaderProps> = ({
  renderRight,
  installedApplication,
  url,
  children,
  application,
}) => {
  const {navigateToLink} = useLinkNavigation();
  const navigateToApplicationsList = useCallback(
    () =>
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.LIST,
          resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          applicationId: undefined,
          searchQuery: undefined,
        }),
      ),
    [navigateToLink],
  );

  const isInApplicationSubComponent =
    url.resourcePage !== SettingsResourcePagesEnum.OVERVIEW ||
    (url.componentPage === SettingsApplicationComponentsEnum.AUTOLINKERS && url.autolinkerId) ||
    (url.componentPage === SettingsApplicationComponentsEnum.PREVIEWERS && url.previewerId) ||
    (url.componentPage === SettingsApplicationComponentsEnum.SERVERS && url.serverId);

  const renderBreadcrumbs = () => {
    const breadcrumbs = isInApplicationSubComponent
      ? [
          {
            label: installedApplication?.name ?? '',
            onBreadcrumbClick: () => {
              const {applicationId} = url;
              if (!applicationId) {
                navigateToApplicationsList();
                return;
              }
              navigateToLink(
                buildSettingsPath({
                  rootPage: SettingsRootPagesEnum.APPLICATIONS,
                  componentPage: url.componentPage,
                  resourcePage: SettingsResourcePagesEnum.OVERVIEW,
                  applicationId,
                  searchQuery: undefined,
                  autolinkerId: undefined,
                  previewerId: undefined,
                  serverId: undefined,
                }),
              );
            },
          },
        ]
      : [];

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="settings-applications-breadcrumb">Applications</Trans>,
          onBreadcrumbClick: navigateToApplicationsList,
        }}
        breadcrumbs={breadcrumbs}
      />
    );
  };

  return (
    <SettingsHeader
      topLine={renderBreadcrumbs()}
      right={renderRight?.()}
      tabs={
        isInApplicationSubComponent ? null : (
          <ApplicationsTab
            componentPage={url.componentPage}
            hasLocalApplication={Boolean(application)}
            applicationId={installedApplication?.id}
          />
        )
      }
    >
      {children ?? <Trans id="applications-edit-title">Edit application {installedApplication?.name}</Trans>}
    </SettingsHeader>
  );
};
